/**
 * Contexte de consultation de la liste d'appels dans l'URL — plan de
 * modernisation UX, §3.
 *
 * Un lien copié depuis la liste, ou produit par la vue d'ensemble, rouvre la
 * liste avec la même période et les mêmes critères. Les droits restent ceux
 * du destinataire : l'URL ne porte que des critères, jamais de données d'appel,
 * et la recherche libre (qui peut contenir un nom de fichier ou un numéro)
 * n'y figure pas.
 */
import type { CallsState } from "@/hooks/useNavigationStore";
import { CALLS_FILTER_DEFAULTS } from "@/hooks/useNavigationStore";
import { parseInputDate, toInputDate } from "@/lib/dateRange";

type ListKey =
  | "selectedTeams"
  | "selectedAgentIds"
  | "selectedReasonParents"
  | "selectedReasonChildren"
  | "resolutionFilter"
  | "reiterationFilter"
  | "segmentFilter"
  | "campaignFilter"
  | "selectedProducts";

/** Nom court et lisible de chaque paramètre. */
const LIST_PARAMS: Record<ListKey, string> = {
  selectedTeams: "equipe",
  selectedAgentIds: "agent",
  selectedReasonParents: "motif",
  selectedReasonChildren: "sous_motif",
  resolutionFilter: "resultat",
  reiterationFilter: "reiteration",
  segmentFilter: "segment",
  campaignFilter: "campagne",
  selectedProducts: "produit",
};

const PARAM_FROM = "du";
const PARAM_TO = "au";
const PARAM_CONVERTED = "conversion";
const PARAM_SORT = "tri";
/** Colonnes de tri acceptées par la RPC : une valeur arbitraire de l'URL est ignorée. */
const SORTABLE_COLUMNS = ["date", "performance", "satisfaction", "reiteration", "duration"];

/** Paramètres d'URL décrivant la période et les critères courants. */
export function callsStateToSearchParams(
  state: Pick<CallsState, ListKey | "dateRange" | "convertedFilter" | "sortColumn" | "sortDirection">
): URLSearchParams {
  const params = new URLSearchParams();
  const from = state.dateRange?.from ? toInputDate(new Date(state.dateRange.from)) : "";
  const to = state.dateRange?.to ? toInputDate(new Date(state.dateRange.to)) : "";
  if (from) params.set(PARAM_FROM, from);
  if (to) params.set(PARAM_TO, to);
  (Object.keys(LIST_PARAMS) as ListKey[]).forEach((key) => {
    state[key]?.forEach((value) => params.append(LIST_PARAMS[key], value));
  });
  if (state.convertedFilter) params.set(PARAM_CONVERTED, state.convertedFilter === "true" ? "oui" : "non");
  if (state.sortColumn !== "date" || state.sortDirection !== "desc") {
    params.set(PARAM_SORT, `${state.sortColumn}:${state.sortDirection}`);
  }
  return params;
}

/** Lien absolu vers la liste d'appels dans ce contexte. */
export function buildCallsUrl(state: Parameters<typeof callsStateToSearchParams>[0], origin = window.location.origin): string {
  const query = callsStateToSearchParams(state).toString();
  return `${origin}/appels${query ? `?${query}` : ""}`;
}

/** Chemin relatif, pour la navigation interne depuis un indicateur. */
export function callsPath(partial: Partial<Parameters<typeof callsStateToSearchParams>[0]>): string {
  const query = callsStateToSearchParams({
    ...CALLS_FILTER_DEFAULTS,
    dateRange: undefined,
    sortColumn: "date",
    sortDirection: "desc",
    ...partial,
  } as Parameters<typeof callsStateToSearchParams>[0]).toString();
  return `/appels${query ? `?${query}` : ""}`;
}

/**
 * État de liste correspondant à une URL, ou `null` si l'URL ne porte aucun
 * critère reconnu. Les critères absents reprennent leur valeur par défaut :
 * ouvrir un lien remplace le contexte, il ne le fusionne pas.
 */
export function searchParamsToCallsState(params: URLSearchParams): Partial<CallsState> | null {
  const known = [PARAM_FROM, PARAM_TO, PARAM_CONVERTED, PARAM_SORT, ...Object.values(LIST_PARAMS)];
  if (!known.some((key) => params.has(key))) return null;

  // Tri et période absents de l'URL reprennent leur valeur neutre : un lien
  // sans période couvre toute la période, un lien sans tri trie par date.
  const next: Partial<CallsState> = {
    ...CALLS_FILTER_DEFAULTS,
    currentPage: 1,
    sortColumn: "date",
    sortDirection: "desc",
  };
  const from = parseInputDate(params.get(PARAM_FROM) ?? "");
  const to = parseInputDate(params.get(PARAM_TO) ?? "");
  next.dateRange = from || to ? { from: from?.toISOString(), to: to?.toISOString() } : undefined;
  (Object.keys(LIST_PARAMS) as ListKey[]).forEach((key) => {
    const values = params.getAll(LIST_PARAMS[key]).filter(Boolean);
    if (values.length > 0) next[key] = values;
  });
  const converted = params.get(PARAM_CONVERTED);
  if (converted === "oui") next.convertedFilter = "true";
  if (converted === "non") next.convertedFilter = "false";
  const sort = params.get(PARAM_SORT);
  if (sort) {
    const [column, direction] = sort.split(":");
    if (column && SORTABLE_COLUMNS.includes(column)) next.sortColumn = column;
    if (direction === "asc" || direction === "desc") next.sortDirection = direction;
  }
  return next;
}
